import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { getToken, setToken } from '../lib/auth';
import { Icon } from '../lib/icons';
import { themeToStyle } from '../lib/theme';
import { useMemorialData, type ProductItem } from '../hooks/useMemorialData';
import { TEMPLATE_MAP } from '../templates';
import DedicateModal from '../components/DedicateModal';
import NotaryBadge from '../components/NotaryBadge';
import TemplateBackground from '../components/TemplateBackground';
import ContentBlocks, { resolveBlocks } from '../components/blocks/ContentBlocks';
export default function Memorial() {
  const { slug } = useParams<{ slug: string }>();
  const {
    memorial,
    offerings,
    media,
    groups,
    theme,
    needPassword,
    error,
    loading,
    loadMemorial,
    loadOfferings,
  } = useMemorialData(slug);

  const [activeCat, setActiveCat] = useState('');
  const [selected, setSelected] = useState<ProductItem | null>(null);
  const [pwd, setPwd] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [unlockToken, setUnlockToken] = useState('');
  /** slug -> 后台自定义的背景 SVG（为空则前端回落内置默认） */
  const [bgMap, setBgMap] = useState<Record<string, string>>({});
  /** slug -> 该模板实际使用的内置布局 key（后台新增模板可复用任意内置布局） */
  const [layoutMap, setLayoutMap] = useState<Record<string, string>>({});
  /** slug -> 模板的区块配置 JSON */
  const [configMap, setConfigMap] = useState<Record<string, string>>({});
  // 默认选中第一个分类
  useEffect(() => {
    if (groups.length > 0 && !activeCat) {
      setActiveCat(groups[0].categoryId);
    }
  }, [groups, activeCat]);

  // 拉取模板的背景与布局（后台均可自定义），叠加在模板内容之下
  useEffect(() => {
    api<{
      items: {
        slug: string;
        bgSvg?: string | null;
        layout?: string | null;
        config?: string | null;
      }[];
    }>('/templates').then((res) => {
      if (res.ok && res.data) {
        const bg: Record<string, string> = {};
        const ly: Record<string, string> = {};
        const cf: Record<string, string> = {};
        res.data.items.forEach((t) => {
          bg[t.slug] = t.bgSvg ?? '';
          ly[t.slug] = t.layout ?? '';
          cf[t.slug] = t.config ?? '';
        });
        setBgMap(bg);
        setLayoutMap(ly);
        setConfigMap(cf);
      }
    });
  }, []);



  async function unlock(e: React.FormEvent) {
    e.preventDefault();
    const res = await api<{ unlockToken: string }>(`/memorials/${slug}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ password: pwd }),
    });
    if (res.ok && res.data) {
      setUnlockToken(res.data.unlockToken);
      await loadMemorial(res.data.unlockToken);
    } else {
      setPwdError(errMsg(res, '密码错误'));
    }
  }

  if (loading) {
    return (
      <Shell>
        <p className="text-sm text-[var(--c-muted)]">加载中…</p>
      </Shell>
    );
  }

  if (needPassword) {
    return (
      <Shell>
        <div className="w-full max-w-sm rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface)] p-8">
          <p className="mb-1 text-center text-3xl"><Icon name="lock" /></p>
          <h1 className="mb-1 text-center font-serifcn text-lg text-[var(--c-text)]">
            该纪念馆需要密码
          </h1>
          <p className="mb-6 text-center text-xs text-[var(--c-muted)]">
            请输入馆主提供的访问密码
          </p>
          <form onSubmit={unlock}>
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="访问密码"
              className="mb-3 h-11 w-full rounded-xl border border-[var(--c-border)] px-3 text-sm outline-none"
            />
            {pwdError && <p className="mb-3 text-xs text-red-600">{pwdError}</p>}
            <button className="h-11 w-full rounded-xl bg-[var(--c-text)] text-sm text-[var(--c-bg)]">
              进入纪念馆
            </button>
          </form>
        </div>
      </Shell>
    );
  }

  if (error || !memorial) {
    return (
      <Shell>
        <div className="text-center">
          <p className="text-4xl"><Icon name="dove" /></p>
          <p className="mt-4 text-sm text-[var(--c-muted)]">{error || '纪念馆不存在'}</p>
          <Link to="/" className="mt-4 inline-block text-xs text-[var(--c-muted)] underline">
            返回首页
          </Link>
        </div>
      </Shell>
    );
  }

  // 按纪念馆配置选择模板；layout 允许后台新增的模板复用某个内置布局
  const layoutKey = layoutMap[memorial.templateId] || memorial.templateId;
  const Template =
    TEMPLATE_MAP[layoutKey] ?? TEMPLATE_MAP[memorial.templateId] ?? TEMPLATE_MAP.classic;

  return (
    <div
      className="min-h-screen bg-[var(--c-bg)] text-[var(--c-text)]"
      style={themeToStyle(theme?.config)}
    >
      <header className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--c-text)] text-xs text-[var(--c-bg)]">
            墓
          </span>
          <span className="text-sm text-[var(--c-muted)]">线上墓迪</span>
        </Link>
        {getToken() ? (
          <span className="flex items-center gap-4">
            {memorial.canEdit && (
              <Link
                to={`/m/${memorial.slug}/edit`}
                className="text-xs text-[var(--c-accent)] underline"
              >
                编辑
              </Link>
            )}
            <Link to="/memorials" className="text-xs text-[var(--c-muted)] underline">
              我的纪念馆
            </Link>
          </span>
        ) : (
          <Link to="/login" className="text-xs text-[var(--c-muted)] underline">
            登录
          </Link>
        )}
      </header>

      <main className="mx-auto max-w-5xl px-6">
        {/* 已存证时展示徽章，点击可查看指纹与区块浏览器查验链接（访客不显示购买入口） */}
        <div className="mt-6">
          <NotaryBadge memorialId={memorial.id} />
        </div>

        <div className="relative isolate">
          <TemplateBackground slug={layoutKey} bgSvg={bgMap[memorial.templateId]} />
          <Template
            memorial={memorial}
            offerings={offerings}
            media={media}
            groups={groups}
            activeCat={activeCat}
            onSelectCat={setActiveCat}
            onPick={(p) => setSelected(p)}
          />
          {/* 可配置内容区块（相册 / 留言墙 / 追思文章），按模板配置渲染在所有模板之上 */}
          <ContentBlocks
            memorial={memorial}
            media={media}
            blocks={resolveBlocks(configMap[memorial.templateId])}
            memorialSlug={slug ?? ''}
          />
          {/* 底部留白：区块在模板之后渲染，需避免末尾内容被固定道具栏遮挡 */}
          <div className="h-40" />
        </div>
      </main>

      {selected && (
        <DedicateModal
          memorialId={memorial.id}
          product={selected}
          onClose={() => setSelected(null)}
          onSuccess={() => loadOfferings(memorial.id)}
          onNeedLogin={() => {
            setToken('');
            window.location.href = '/login';
          }}
        />
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--c-bg)] px-6">
      {children}
    </div>
  );
}
